import React, { forwardRef, useMemo, useState } from "react";
import { interpolate } from "../../utils/interpolate";
import { DynamicFormWithGalleryProps } from "./DynamicFormWithGallery.types";
import { GalleryImageConfig } from "../../utils/image/types";
import {
  DynamicFormWithGalleryContainer,
  ContentWrapper,
  GalleryWrapper,
  FormWrapper,
  TitleSection,
  Title,
  Subtitle,
  Background,
  Shadow,
  SingleImageWrapper,
  HighlightSlotsCard,
  HighlightSlotsTopRow,
  HighlightSlot,
  HighlightSlotTitle,
  HighlightSlotDescription,
  HighlightVerticalDivider,
  HighlightHorizontalDivider,
  HighlightBottomSlot,
  HighlightBottomTitle,
  HighlightBottomDescription,
  ComplementaryTextWrapper,
  ComplementaryTextDescription,
  HighlightSelect,
} from "./DynamicFormWithGallery.styles";
import {
  HighlightBox,
  HighlightPreTitle,
  HighlightTitle,
} from "../DynamicForm/DynamicForm.styles";
import { BackgroundVideo } from "../../components/BackgroundVideo/BackgroundVideo";
import { GallerySection } from "./GallerySection";
import { CountdownSection } from "./CountdownSection";
import { FormBuilder } from "../../components/FormBuilder";
import { formatGtagLabel } from "../../utils/formatGtagLabel";
import { activeHidden } from "../common/BlockVisibility";

const DynamicFormWithGalleryInner = (
  {
    dynamicStructure,
    images,
    image,
    imageVisible,
    title,
    subTitle,
    titleVisible,
    subTitleVisible,
    imagesVisible,
    titleTag = "h2",
    carouselConfig,
    showThumbnails,
    enableLightbox,
    elementColor,
    imageAspectRatio,
    submitUrl,
    privacyPolicy,
    buttonLabel,
    selectedFormId,
    form: formToRender,
    afterRedirect,
    backgroundImage,
    backgroundColor,
    backgroundType: rawBackgroundType = "color",
    typeVideo,
    urlVideo,
    video,
    thumb,
    altThumb,
    embedVideo,
    loopVideo,
    audioVideo,
    shadowColor,
    colorConfig,
    formStyles,
    palette,
    padding,
    activePalette,
    hideOnMobile,
    hideOnDesktop,
    htmlId,
    highlightVisible,
    highlightPreTitle,
    highlightTitle,
    highlightPreTitleColor,
    highlightTitleColor,
    highlightBgColor,
    highlightSelectTextColor,
    highlightConfig,
    product,
    category,
    channel,
    siteId,
    teamId,
    moduleId,
    accountId,
    contentEntryId,
    formId,
    antiBot,
    highlightSlotsVisible,
    highlightSlots,
    complementaryTextVisible,
    complementaryText,
    selectLabel,
    selectOptions: selectOptionsProp,
    selectVisible: selectVisibleProp,
    relatedContentConfig,
    relatedSlotConfig,
    relatedComplementaryTextField,
    relatedSelectFieldName,
    relatedEntriesMap,
    relatedMediaMap,
    isMobile,
    dynamicFields,
    countdownConfig,
    countdownColors,
    useGlobalQueryParams,
    ...props
  }: DynamicFormWithGalleryProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const [selectedValue, setSelectedValue] = useState("");

  const backgroundType =
    rawBackgroundType === "cmsImage"
      ? "image"
      : rawBackgroundType === "cmsVideo"
        ? "video"
        : rawBackgroundType;

  const extractFieldName = (pattern: string): string | null => {
    const match = pattern.match(/\{\{item\.(.+?)(?:\|.*)?\}\}/);
    return match ? match[1] : null;
  };

  const selectOptions = useMemo(() => {
    if (selectOptionsProp && selectOptionsProp.length > 0) {
      return selectOptionsProp;
    }

    const relationId = relatedContentConfig?.contentTypeRelationId;
    if (!relationId) return [];

    const relatedData = dynamicStructure?.item?.relations?.[relationId];
    if (!relatedData?.items) return [];

    const fieldNamePattern =
      relatedSelectFieldName || relatedContentConfig?.selectFieldName || "name";
    const fieldName = extractFieldName(fieldNamePattern) || fieldNamePattern;

    return relatedData.items.map((item: any) => ({
      label: item[fieldName] || item.slug || item.id,
      value: item.id || item.slug,
    }));
  }, [
    selectOptionsProp,
    dynamicStructure?.item?.relations,
    relatedContentConfig?.contentTypeRelationId,
    relatedSelectFieldName,
    relatedContentConfig?.selectFieldName,
  ]);

  const selectVisible = selectVisibleProp !== false && selectOptions.length > 0;

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValue(e.target.value);
  };

  // Sem interação o select nativo mostra a primeira opção, então é ela que vale para destaques,
  // imagem e lead: o que o visitante vê é o que a conversão leva. Com o select oculto não há
  // escolha nenhuma, e bloco e lead ficam com o registro da página.
  const effectiveValue = selectVisible
    ? selectedValue || selectOptions[0]?.value
    : undefined;

  const selectedRelatedEntry = useMemo(() => {
    const relationId = relatedContentConfig?.contentTypeRelationId;

    if (!effectiveValue || !relationId) return undefined;

    const relatedData = dynamicStructure?.item?.relations?.[relationId];
    return relatedData?.items
      ? relatedData.items.find(
          (item: any) =>
            item.id === effectiveValue || item.slug === effectiveValue,
        )
      : relatedEntriesMap?.[effectiveValue];
  }, [
    effectiveValue,
    dynamicStructure,
    relatedContentConfig?.contentTypeRelationId,
    relatedEntriesMap,
  ]);

  const selectedEntryDynamicStructure = useMemo(() => {
    if (!selectedRelatedEntry) return dynamicStructure;

    return { ...dynamicStructure, item: selectedRelatedEntry };
  }, [selectedRelatedEntry, dynamicStructure]);

  // O lead registra como interesse a opção escolhida (ex.: a versão), não o registro da página.
  // O id da entry vem do `_metadata` (o `id` do item da relação é o slug no site publicado).
  const selectedRelatedEntryId = (
    selectedRelatedEntry as { _metadata?: { id?: string } } | undefined
  )?._metadata?.id;

  const selectedMedia = effectiveValue
    ? relatedMediaMap?.[effectiveValue]
    : undefined;
  const selectedMediaIsGallery = Boolean(selectedMedia?.images?.length);
  const selectedMediaIsImage =
    !selectedMediaIsGallery && Boolean(selectedMedia?.image?.props?.src);
  const mediaFromSelection = selectedMediaIsGallery || selectedMediaIsImage;
  const mediaField = mediaFromSelection
    ? "relatedContentConfig/imageField"
    : "images";

  const resolvedSlots = useMemo(() => {
    if (!relatedSlotConfig) return highlightSlots || {};

    const selectedEntry = selectedEntryDynamicStructure?.item;
    if (!selectedEntry || selectedEntry === dynamicStructure?.item)
      return highlightSlots || {};

    const resolveField = (
      field: string | undefined,
      fallback: string | undefined,
    ) => (field ? selectedEntry[field] : fallback);

    return {
      slot1: {
        title: resolveField(
          relatedSlotConfig.slot1TitleField,
          highlightSlots?.slot1?.title,
        ),
        titleVisible: highlightSlots?.slot1?.titleVisible ?? true,
        description: resolveField(
          relatedSlotConfig.slot1DescriptionField,
          highlightSlots?.slot1?.description,
        ),
        descriptionVisible: highlightSlots?.slot1?.descriptionVisible ?? true,
      },
      slot2: {
        title: resolveField(
          relatedSlotConfig.slot2TitleField,
          highlightSlots?.slot2?.title,
        ),
        titleVisible: highlightSlots?.slot2?.titleVisible ?? true,
        description: resolveField(
          relatedSlotConfig.slot2DescriptionField,
          highlightSlots?.slot2?.description,
        ),
        descriptionVisible: highlightSlots?.slot2?.descriptionVisible ?? true,
      },
      slot3: {
        title: resolveField(
          relatedSlotConfig.slot3TitleField,
          highlightSlots?.slot3?.title,
        ),
        titleVisible: highlightSlots?.slot3?.titleVisible ?? true,
        description: resolveField(
          relatedSlotConfig.slot3DescriptionField,
          highlightSlots?.slot3?.description,
        ),
        descriptionVisible: highlightSlots?.slot3?.descriptionVisible ?? true,
      },
    };
  }, [
    selectedEntryDynamicStructure,
    relatedSlotConfig,
    highlightSlots,
    dynamicStructure?.item,
  ]);

  const processedImages = useMemo<GalleryImageConfig[]>(() => {
    if (selectedMediaIsGallery) return selectedMedia!.images!;
    if (Array.isArray(images)) {
      return images;
    }
    return [];
  }, [images, selectedMedia, selectedMediaIsGallery]);

  const transformedBackgroundImage = useMemo(() => {
    if (!backgroundImage) return null;

    if (backgroundImage.desktop && backgroundImage.mobile) {
      return backgroundImage;
    }
  }, [backgroundImage]);

  const transformedImage = useMemo(() => {
    if (selectedMediaIsGallery) return null;
    if (selectedMediaIsImage) return selectedMedia!.image!;
    return image || null;
  }, [image, selectedMedia, selectedMediaIsGallery, selectedMediaIsImage]);

  const hasSingleImage = useMemo(() => {
    return Boolean(transformedImage?.props?.src);
  }, [transformedImage]);

  const resolvedMediaMode = useMemo<"image" | "gallery" | undefined>(() => {
    if (hasSingleImage) return "image";
    if (processedImages.length > 0) return "gallery";
    return undefined;
  }, [hasSingleImage, processedImages.length]);

  const processedForm = formToRender;

  const countdownUnpublishAt = dynamicStructure?.item?.unpublishAt as
    | string
    | undefined;
  const renderCountdownAt = (
    pos: "aboveTitle" | "aboveImage" | "belowImage" | "aboveForm",
  ) =>
    countdownConfig && (countdownConfig.position || "belowImage") === pos ? (
      <CountdownSection
        config={countdownConfig}
        colors={countdownColors}
        unpublishAt={countdownUnpublishAt}
      />
    ) : null;

  return (
    <DynamicFormWithGalleryContainer
      ref={ref}
      id={htmlId}
      className={`dynamic-form-with-gallery-section ${activePalette ? activePalette : ""} ${activeHidden({ hideOnMobile, hideOnDesktop })}`}
      backgroundColor={backgroundColor}
      backgroundType={backgroundType}
      padding={padding}
      {...props}
    >
      {transformedBackgroundImage && backgroundType === "image" && (
        <Background>
          {transformedBackgroundImage?.desktop?.render({
            'data-af-field': 'backgroundImage/desktop/props/src',
            ...transformedBackgroundImage.desktop.props,
            className: "desktop-image image",
          })}
          {transformedBackgroundImage?.mobile?.render({
            'data-af-field': 'backgroundImage/mobile/props/src',
            ...transformedBackgroundImage.mobile.props,
            className: "mobile-image image",
          })}
        </Background>
      )}

      {backgroundType === "video" && (
        <>
          {typeVideo === "url" && (
            <BackgroundVideo
              url={urlVideo}
              loop={loopVideo}
              audio={audioVideo}
              data-testid="media-section-dynamic-background-video-url"
            />
          )}
          {typeVideo === "embed" && (
            <BackgroundVideo
              embed={embedVideo}
              loop={loopVideo}
              audio={audioVideo}
              data-testid="media-section-dynamic-background-video-embed"
            />
          )}
        </>
      )}

      {(backgroundType === "video" || backgroundType === "image") && (
        <Shadow shadowColor={shadowColor} />
      )}

      <ContentWrapper data-anim-stagger>
        <GalleryWrapper>
          <TitleSection>
            {renderCountdownAt("aboveTitle")}
            {titleVisible !== false &&
              !!title?.length &&
              (!!colorConfig?.titleColor?.length ||
                !!activePalette?.length) && (
                <Title
                  data-af-field="title"
                  titleTag={titleTag}
                  color={colorConfig?.titleColor}
                >
                  {
                    interpolate({
                      structure: dynamicStructure || {},
                      template: title,
                    }).node
                  }
                </Title>
              )}
            {subTitleVisible !== false &&
              !!subTitle?.length &&
              (!!colorConfig?.subTitleColor?.length ||
                !!activePalette?.length) && (
                <Subtitle
                  data-af-field="subTitle"
                  titleTag="div"
                  color={colorConfig?.subTitleColor}
                  className="rich-text-reset-html"
                >
                  {
                    interpolate(
                      {
                        structure: dynamicStructure || {},
                        template: subTitle,
                      },
                      { parseHtml: true },
                    ).node
                  }
                </Subtitle>
              )}
          </TitleSection>

          {renderCountdownAt("aboveImage")}

          {resolvedMediaMode === "gallery" &&
            imagesVisible !== false &&
            processedImages.length > 0 && (
              <GallerySection
                // A galeria da opção escolhida remonta o carrossel no primeiro slide.
                key={mediaFromSelection ? effectiveValue : undefined}
                data-af-field={mediaField}
                images={processedImages}
                carouselConfig={carouselConfig}
                showThumbnails={showThumbnails}
                enableLightbox={enableLightbox}
                elementColor={elementColor}
                isMobile={isMobile}
                imageAspectRatio={imageAspectRatio}
              />
            )}

          {resolvedMediaMode === "image" &&
            transformedImage &&
            imageVisible !== false && (
              <SingleImageWrapper $imageAspectRatio={imageAspectRatio}>
                {transformedImage.render({
                  ...transformedImage.props,
                  className: "single-image",
                  "data-af-field": mediaField,
                  style: {
                    ...transformedImage.props.style,
                    width: "100%",
                    height: "auto",
                    display: "block",
                  },
                })}
              </SingleImageWrapper>
            )}

          {renderCountdownAt("belowImage")}

          {selectVisible && (
            <HighlightSelect
              label={
                selectLabel ||
                relatedContentConfig?.selectLabel ||
                "Selecione uma opção"
              }
              colorText={
                highlightSelectTextColor || "var(--text-title, #0f172a)"
              }
              colorLabel={highlightSelectTextColor || undefined}
              value={selectedValue}
              onChange={handleSelectChange}
              options={
                selectOptions?.length
                  ? selectOptions
                  : [
                      {
                        label: relatedContentConfig?.contentTypeRelationId
                          ? "Carregando..."
                          : "Nenhuma opção disponível",
                        value: "",
                      },
                    ]
              }
            />
          )}

          {highlightSlotsVisible &&
            (() => {
              const slot1ShouldRender =
                ((resolvedSlots.slot1?.titleVisible ?? true) &&
                  resolvedSlots.slot1?.title) ||
                ((resolvedSlots.slot1?.descriptionVisible ?? true) &&
                  resolvedSlots.slot1?.description);
              const slot2ShouldRender =
                ((resolvedSlots.slot2?.titleVisible ?? true) &&
                  resolvedSlots.slot2?.title) ||
                ((resolvedSlots.slot2?.descriptionVisible ?? true) &&
                  resolvedSlots.slot2?.description);
              const slot3ShouldRender =
                ((resolvedSlots.slot3?.titleVisible ?? true) &&
                  resolvedSlots.slot3?.title) ||
                ((resolvedSlots.slot3?.descriptionVisible ?? true) &&
                  resolvedSlots.slot3?.description);

              if (
                !slot1ShouldRender &&
                !slot2ShouldRender &&
                !slot3ShouldRender
              )
                return null;

              return (
                <HighlightSlotsCard>
                  {(slot1ShouldRender || slot2ShouldRender) && (
                    <HighlightSlotsTopRow>
                      {slot1ShouldRender && (
                        <HighlightSlot data-af-field="highlightSlots/slot1">
                          {(resolvedSlots.slot1?.titleVisible ?? true) &&
                            resolvedSlots.slot1?.title && (
                              <HighlightSlotTitle data-af-field="highlightSlots/slot1/title">
                                {
                                  interpolate({
                                    structure: selectedEntryDynamicStructure!,
                                    template: resolvedSlots.slot1?.title || "",
                                  }).node
                                }
                              </HighlightSlotTitle>
                            )}
                          {(resolvedSlots.slot1?.descriptionVisible ?? true) &&
                            resolvedSlots.slot1?.description && (
                              <HighlightSlotDescription data-af-field="highlightSlots/slot1/description">
                                {
                                  interpolate({
                                    structure: selectedEntryDynamicStructure!,
                                    template:
                                      resolvedSlots.slot1?.description || "",
                                  }).node
                                }
                              </HighlightSlotDescription>
                            )}
                        </HighlightSlot>
                      )}

                      {slot1ShouldRender && slot2ShouldRender && (
                        <HighlightVerticalDivider />
                      )}

                      {slot2ShouldRender && (
                        <HighlightSlot data-af-field="highlightSlots/slot2">
                          {(resolvedSlots.slot2?.titleVisible ?? true) &&
                            resolvedSlots.slot2?.title && (
                              <HighlightSlotTitle data-af-field="highlightSlots/slot2/title">
                                {
                                  interpolate({
                                    structure: selectedEntryDynamicStructure!,
                                    template: resolvedSlots.slot2?.title || "",
                                  }).node
                                }
                              </HighlightSlotTitle>
                            )}
                          {(resolvedSlots.slot2?.descriptionVisible ?? true) &&
                            resolvedSlots.slot2?.description && (
                              <HighlightSlotDescription data-af-field="highlightSlots/slot2/description">
                                {
                                  interpolate({
                                    structure: selectedEntryDynamicStructure!,
                                    template:
                                      resolvedSlots.slot2?.description || "",
                                  }).node
                                }
                              </HighlightSlotDescription>
                            )}
                        </HighlightSlot>
                      )}
                    </HighlightSlotsTopRow>
                  )}

                  {(slot1ShouldRender || slot2ShouldRender) &&
                    slot3ShouldRender && <HighlightHorizontalDivider />}

                  {slot3ShouldRender && (
                    <HighlightBottomSlot data-af-field="highlightSlots/slot3">
                      {(resolvedSlots.slot3?.titleVisible ?? true) &&
                        resolvedSlots.slot3?.title && (
                          <HighlightBottomTitle data-af-field="highlightSlots/slot3/title">
                            {
                              interpolate({
                                structure: selectedEntryDynamicStructure!,
                                template: resolvedSlots.slot3?.title || "",
                              }).node
                            }
                          </HighlightBottomTitle>
                        )}
                      {(resolvedSlots.slot3?.descriptionVisible ?? true) &&
                        resolvedSlots.slot3?.description && (
                          <HighlightBottomDescription data-af-field="highlightSlots/slot3/description">
                            {
                              interpolate({
                                structure: selectedEntryDynamicStructure!,
                                template:
                                  resolvedSlots.slot3?.description || "",
                              }).node
                            }
                          </HighlightBottomDescription>
                        )}
                    </HighlightBottomSlot>
                  )}
                </HighlightSlotsCard>
              );
            })()}

          {complementaryTextVisible !== false && complementaryText && (
            <ComplementaryTextWrapper>
              <ComplementaryTextDescription className="rich-text-reset-html" data-af-field="complementaryText">
                {
                  interpolate(
                    {
                      structure: dynamicStructure || {},
                      template: complementaryText,
                    },
                    { parseHtml: true },
                  ).node
                }
              </ComplementaryTextDescription>
            </ComplementaryTextWrapper>
          )}
        </GalleryWrapper>

        <div
          style={{
            width: "490px",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {renderCountdownAt("aboveForm")}

          {(highlightVisible !== false || highlightConfig?.visible) &&
            (highlightPreTitle ||
              highlightTitle ||
              highlightConfig?.preTitle ||
              highlightConfig?.title) && (
              <HighlightBox
                $bgColor={highlightBgColor || highlightConfig?.bgColor}
              >
                {(highlightPreTitle || highlightConfig?.preTitle) && (
                  <HighlightPreTitle
                    as="div"
                    data-af-field="highlightPreTitle"
                    $color={
                      highlightPreTitleColor || highlightConfig?.preTitleColor
                    }
                  >
                    {
                      interpolate(
                        {
                          structure: dynamicStructure || {},
                          template:
                            highlightPreTitle ||
                            highlightConfig?.preTitle ||
                            "",
                        },
                        { parseHtml: true },
                      ).node
                    }
                  </HighlightPreTitle>
                )}
                {(highlightTitle || highlightConfig?.title) && (
                  <HighlightTitle
                    as="div"
                    data-af-field="highlightTitle"
                    $color={highlightTitleColor || highlightConfig?.titleColor}
                  >
                    {
                      interpolate(
                        {
                          structure: dynamicStructure || {},
                          template:
                            highlightTitle || highlightConfig?.title || "",
                        },
                        { parseHtml: true },
                      ).node
                    }
                  </HighlightTitle>
                )}
              </HighlightBox>
            )}
          <FormWrapper bgColor={formStyles?.bgColor} data-af-field="selectedFormId">
            <FormBuilder
              useGlobalQueryParams={useGlobalQueryParams}
              form={processedForm}
              submitUrl={submitUrl}
              buttonLabel={buttonLabel}
              privacyPolicy={privacyPolicy}
              afterRedirect={afterRedirect}
              dynamicStructure={(() => {
                const selectedOption = selectOptions.find(
                  (opt: { label: string; value: string }) =>
                    opt.value === effectiveValue,
                );

                return {
                  ...dynamicStructure,
                  ...(effectiveValue &&
                    selectedRelatedEntry && {
                      relatedSelection: {
                        id: effectiveValue,
                        ...selectedRelatedEntry,
                      },
                    }),
                  ...(selectedOption && {
                    selectedHighlightLabel: selectedOption.label,
                  }),
                };
              })()}
              formStyles={formStyles}
              palette={palette}
              product={product}
              category={category}
              channel={channel}
              siteId={siteId}
              teamId={teamId}
              moduleId={moduleId}
              accountId={accountId}
              contentEntryId={selectedRelatedEntryId ?? contentEntryId}
              pageContentEntryId={
                selectedRelatedEntryId ? contentEntryId : undefined
              }
              formId={formId}
              antiBot={antiBot}
              gtagLabel={formatGtagLabel(
                htmlId,
                interpolate({
                  structure: dynamicStructure || {},
                  template: title || "",
                }).text,
                buttonLabel || "Enviar",
              )}
              dynamicFields={dynamicFields}
            />
          </FormWrapper>
        </div>
      </ContentWrapper>
    </DynamicFormWithGalleryContainer>
  );
};

export const DynamicFormWithGallery = forwardRef(
  DynamicFormWithGalleryInner,
) as (
  props: DynamicFormWithGalleryProps & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  },
) => ReturnType<typeof DynamicFormWithGalleryInner>;
